import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';

import { ModalRef, ModalService } from '@ridango/components/lib/core-ui';
import {
    LocalWebTicket,
    LogService,
    Ticket,
    UserTicketAction,
    UserTicketActionsEnum,
    UserTicketActionCarrier
} from '@ridango/core';

import { TicketFocusService } from '../../ticket-focus.service';

@Component({
    selector: 'ridango-ticket',
    templateUrl: './ticket.component.html'
})
export class TicketComponent implements OnInit {
    private deleteConfirmationDialog: ModalRef;
    private pauseModal: ModalRef;

    private qrModal: ModalRef;
    private zoneModal: ModalRef;

    type = 'SINGLE';
    context = 'ticket';
    deleteAction: UserTicketActionCarrier = null;
    pauseAction: UserTicketActionCarrier = null;
    qrAction: UserTicketActionCarrier = null;

    openTickets = false;

    @Input() ticket: LocalWebTicket;
    @Input() locale = 'en'; //  2 letter language code, lets default to en (english)
    @Input() cards = []; // cards that the ticket is being bought to. currently corporate only.
    @Input() ticketsByDocNums = [];
    @Input() progress: number;
    @Input() actions: UserTicketAction[] = [];
    @Input() useInputActions = false;
    @Input() showActionDropdownAlways = false;
    @Input() transportDayEndPrevDate = false;
    @Input() productType = null;
    @Input() showValidityDayName = true;
    @Input() showValidityStart = true;
    @Input() useActiveValidationText = false;
    @Input() hasZoneModal = false;
    @Input() assetsPath = '';

    @Input() embeddedQR = false;

    @Input() canDeleteFromCart = false;

    @Output() requestDeleteTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestPauseTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestReactivateTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestRenewTicket = new EventEmitter<LocalWebTicket>();
    @Output() requestRemoveCard = new EventEmitter<any>();
    @Output() requestDisplayQR = new EventEmitter<any>();
    @Output() requestTicketAction: EventEmitter<UserTicketActionCarrier> = new EventEmitter<UserTicketActionCarrier>();

    @ViewChild('deleteModalFocus') deleteModalFocusEl;
    @ViewChild('pauseModalFocus') pauseModalFocusEl;
    @ViewChild('qrModalFocus') qrModalFocusEl;

    @ViewChild('periodTicket') periodTicketEl;

    @ViewChild('deleteConfirmation', { read: TemplateRef, static: true }) deleteConfirmationRef: TemplateRef<any>;
    @ViewChild('pauseModal', { read: TemplateRef, static: true }) pauseModalRef: TemplateRef<any>;

    @ViewChild('qrModal', { read: TemplateRef, static: true }) qrModalRef: TemplateRef<any>;
    @ViewChild('qrModalEl') qrModalEl;

    @ViewChild('zoneModal', { read: TemplateRef, static: true }) zoneModalRef: TemplateRef<any>;
    @ViewChild('zoneModalEl') zoneModalEl;

    constructor(
        @Inject('ENV') public env: any,
        private ls: LogService,
        private modalService: ModalService,
        private elRef: ElementRef,
        private ticketFocusService: TicketFocusService
    ) {}

    ngOnInit() {
        if (
            (this.ticket.product_type && this.ticket.product_type === 'W') ||
            (this.ticket.product_object && this.ticket.product_object?.product_type === 'W') ||
            (this.ticket.product && this.ticket.product.product_type === 'W')
        ) {
            this.type = 'WALLET';
        } else if (
            this.ticket.product &&
            this.ticket.product.product_properties &&
            this.ticket.product.product_properties.filter((pp) => pp.code === 'BIKE_GARAGE_PRODUCT').length > 0
        ) {
            // this.type = 'BIKE_GARAGE'; // will leave this here on purpose, when refactoring of 'generic simple ticket' is done
            this.type = 'WALLET'; // reusing currently wallet component, as they are same design wise
        } else if (
            (this.ticket.product_type && this.ticket.product_type === 'H') ||
            (this.ticket.product_object && this.ticket.product_object.product_type === 'H') ||
            (this.ticket.product && this.ticket.product.product_type === 'H')
        ) {
            this.type = 'HOURS';
        } else if (!this.ticket.ticket_details) {
            if (this.productType && this.productType === 'H') {
                this.type = 'HOURS';
            } else {
                this.type = 'PERIOD';
            }
        }
    }

    delete(ticket) {
        this.requestDeleteTicket.emit(ticket);
    }

    pause() {
        this.requestPauseTicket.emit(this.ticket);
        this.closePauseModal();
    }

    reactivate() {
        this.requestReactivateTicket.emit(this.ticket);
    }

    renew() {
        this.requestRenewTicket.emit(this.ticket);
    }

    toggleOpenTickets() {
        this.openTickets = !this.openTickets;
    }

    removeCard(subject) {
        // can be card id or doc_num
        this.requestRemoveCard.emit(subject);
    }

    qr(ticket) {
        this.requestDisplayQR.emit(ticket);
    }

    ticketAction(action: UserTicketActionCarrier) {
        if (action.action.type === UserTicketActionsEnum.DELETE) {
            this.deleteAction = action;
            this.showDeleteModal();
        } else if (action.action.type === UserTicketActionsEnum.QR) {
            if (this.embeddedQR) {
                this.qrAction = action;
                this.showQrModal();
            } else {
                this.requestTicketAction.emit(action);
            }
        } else if (action.action.type === UserTicketActionsEnum.PAUSE) {
            this.pauseAction = action;
            this.showPauseModal();
        } else if (action.action.type === UserTicketActionsEnum.REACTIVATE) {
            this.reactivate();
        } else if (action.action.type === UserTicketActionsEnum.RENEW) {
            this.renew();
        } else {
            this.requestTicketAction.emit(action);
        }
    }
    showDeleteModal() {
        this.deleteConfirmationDialog = this.modalService.open(this.deleteConfirmationRef);
        setTimeout(() => this.deleteModalFocusEl.nativeElement.focus());
    }

    deleteTicket() {
        this.requestTicketAction.emit(this.deleteAction);
        this.deleteConfirmationDialog.close();
    }
    closeModal() {
        this.deleteConfirmationDialog.close();
        this.ticketFocusService.focus();
        // setTimeout(() => this.periodTicketEl.ticketActionsEl.actionLinkEl.nativeElement.focus());
    }

    showPauseModal() {
        this.pauseModal = this.modalService.open(this.pauseModalRef);
        setTimeout(() => this.pauseModalFocusEl.nativeElement.focus());
    }

    pauseTicket() {
        this.requestTicketAction.emit(this.pauseAction);
        this.closePauseModal();
    }
    closePauseModal() {
        this.pauseModal.close();
        this.ticketFocusService.focus();
    }

    showQrModal() {
        this.qrModal = this.modalService.open(this.qrModalRef);
        setTimeout(() => this.qrModalEl.firstFocus.nativeElement.focus());
        // setTimeout(() => this.qrModalFocusEl.nativeElement.focus());
    }

    closeQrModal() {
        this.qrModal.close();
        this.ticketFocusService.focus();
    }

    showZoneModal() {
        this.zoneModal = this.modalService.open(this.zoneModalRef);
        setTimeout(() => this.zoneModalEl.firstFocus.nativeElement.focus());
    }

    closeZoneModal() {
        this.zoneModal.close();
        this.ticketFocusService.focus();
    }
}
